import { FC, useEffect, useRef, useState } from "react";
import Barcode from "react-barcode";
import classNames from "classnames";
import { FreeMode, Navigation, Thumbs } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { Swiper as SwiperRef } from "swiper";

import { Accordeon } from "components/Accordeon";
import { Tabs } from "components/Tabs";
import {
  getNomenclatureClient,
  TNomenclatureClient,
  TNomenclatureClientBrand,
  TNomenclatureClientImage,
} from "services/priceApi";
import { toast } from "react-toastify";
import { Spinner2 } from "components/Spinner2";
import { NoImage } from "components/NoImage";

import { ReactComponent as ArrowIcon } from "assets/img/svg/arrow.svg";

import { Related } from "../Related";

import styles from "./productModal.module.scss";
import { Characteristics, TCharacteristic } from "./Characteristics";
import { Applicabilty } from "./Applicability";
import { Oem } from "./Oem";

import "swiper/css";
import "swiper/css/free-mode";
import "swiper/css/navigation";
import "swiper/css/thumbs";

export type TBrand = TNomenclatureClientBrand;
export type TImage = TNomenclatureClientImage;
/** Карточка из GET /api/v2/nomenclature/client/{id} (раньше nomenclature.getOneClient). */
export type TNomenclatureFull = TNomenclatureClient;

function primaryEanBarcode(
  entries: TNomenclatureClient["barcode"] | undefined
): string | null {
  const v = entries?.[0]?.barcode;
  if (v == null || String(v).trim() === "") return null;
  return String(v);
}

interface IProductModal {
  nomenclatureId: string;
  setNomenclatureId: (nomenclatureId: string) => void;
  breakpoint?: string;
}

export const ProductModal: FC<IProductModal> = ({
  nomenclatureId,
  setNomenclatureId,
  breakpoint = "desktop"
}) => {
  const [thumbsSwiper, setThumbsSwiper] = useState<SwiperRef | null>(null);
  const [nomenclature, setNomenclature] = useState<TNomenclatureFull | null>(
    null
  );
  const [loading, setLoading] = useState(false);
  const navigationPrevRef = useRef<HTMLDivElement>(null);
  const navigationNextRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!nomenclatureId) return;
    setLoading(true);
    getNomenclatureClient(nomenclatureId)
      .then((res) => {
        setNomenclature(res);
      })
      .catch((e: any) => toast.error(e.response?.data?.message))
      .finally(() => {
        setLoading(false);
        // setNomenclatureId("");
      });
  }, [nomenclatureId]);

  const eanBarcode = nomenclature
    ? primaryEanBarcode(nomenclature.barcode)
    : null;

  return (
    <div className={classNames(styles.product, styles[breakpoint])}>
      {loading && <Spinner2 overlay />}
      <div className={styles.top}>

        <div className={styles.caroucelBlock}>
          {breakpoint === "desktop" &&
            <div className={styles.thumbsWrapper}>
              <div ref={navigationPrevRef} className={styles.prevButton} ><ArrowIcon /></div>
              <Swiper
                onSwiper={setThumbsSwiper}
                spaceBetween={10}
                slidesPerView={3}
                freeMode={true}
                watchSlidesProgress={true}
                modules={[FreeMode, Navigation, Thumbs]}
                className={styles.thumbs}
                direction={"vertical"}
              >
                {(nomenclature?.images && nomenclature?.images.length) ? (
                  nomenclature?.images.map((img, i) => (
                    <SwiperSlide className={styles.thumb} key={i}>
                      <img src={`${process.env.REACT_APP_BASE_URL}/api/v2/file/view?file=${img.key}`} alt="" />
                    </SwiperSlide>

                  ))
                ) : (
                  <NoImage className={styles.noImageThumb} />
                )}
              </Swiper>
              <div ref={navigationNextRef} className={styles.nextButton}><ArrowIcon /></div>
            </div>
          }

          <Swiper
            spaceBetween={10}
            thumbs={{ swiper: thumbsSwiper }}
            modules={[FreeMode, Navigation, Thumbs]}
            className={styles.slideWrapper}
            navigation={{
              prevEl: navigationPrevRef.current!,
              nextEl: navigationNextRef.current!,
            }}
          >
            {(nomenclature?.images && nomenclature?.images.length) ? (
              nomenclature?.images.map((img, i) => (
                <SwiperSlide className={styles.slide} key={i}>
                  <img src={`${process.env.REACT_APP_BASE_URL}/api/v2/file/view?file=${img.key}`} alt="" />
                </SwiperSlide>

              ))
            ) : (
              <NoImage className={styles.noImage} />
            )}
          </Swiper>
          {breakpoint !== "desktop" &&
            <div className={styles.thumbsWrapper}>
              <Swiper
                onSwiper={setThumbsSwiper}
                spaceBetween={10}
                slidesPerView={"auto"}
                freeMode={true}
                watchSlidesProgress={true}
                modules={[FreeMode, Navigation, Thumbs]}
                className={styles.thumbs}
              >
                {(nomenclature?.images && nomenclature?.images.length) ? (
                  nomenclature?.images.map((img, i) => (
                    <SwiperSlide className={styles.thumb} key={i}>
                      <img src={`${process.env.REACT_APP_BASE_URL}/api/v2/file/view?file=${img.key}`} alt="" />
                    </SwiperSlide>

                  ))
                ) : (
                  <NoImage className={styles.noImageThumb} />
                )}
              </Swiper>
            </div>
          }

        </div>

        <div className={styles.infoBlock}>

          {breakpoint === "desktop" ? <>
            <div className={styles.article}>Артикул: {nomenclature?.article}</div>
            <div className={styles.brand}>Бренд: {nomenclature?.brand?.name}</div>
          </>
            : <div className={styles.articlesBlock}>
              <div className={styles.brand}>Бренд: {nomenclature?.brand?.name}</div>
              <div className={styles.article}>Артикул: {nomenclature?.article}</div>
            </div>
          }
          {breakpoint === "desktop" ? <>
            <div className={styles.name}>{nomenclature?.name}</div>
            <div className={styles.barcode}>
              {eanBarcode && (
                <Barcode
                  value={eanBarcode}
                  width={1.7}
                  height={70}
                  fontSize={13}
                  format="EAN13"
                />
              )}
            </div>
          </> :
            <div className={styles.nameBlock}>
              <div className={styles.name}>{nomenclature?.name}</div>
              {eanBarcode && (
                <Barcode
                  value={eanBarcode}
                  width={1.7}
                  height={70}
                  fontSize={13}
                  marginTop={-10}
                  format="EAN13"
                />
              )}
            </div>
          }
        </div>
      </div>
      <div className={styles.bottom}>
        {nomenclature && (
          <>
            {breakpoint === "desktop" ? (
              <Tabs
                tabs={[
                  "Техническая информация",
                  "Применяемость",
                  "ОЕМ Номера",
                  "Сопутствующие товары"
                ]}
              >
                <div className={styles.tabContent}>
                  <Characteristics
                    characteristics={nomenclature?.characteristicsValue ?? []}
                  />
                </div>
                <div className={styles.tabContent}>
                  <Applicabilty />
                </div>
                <div className={styles.tabContent}>
                  <Oem />
                </div>
                <div className={styles.tabContent}>
                  <Related products={nomenclature?.relatedProducts ?? []} />
                </div>
              </Tabs>
            ) : (
              <Accordeon
                titles={[
                  "Техническая информация",
                  "Применяемость",
                  "ОЕМ Номера",
                  "Сопутствующие товары"
                ]}
              >
                <Characteristics
                  characteristics={nomenclature?.characteristicsValue ?? []}
                />
                <Applicabilty />
                <Oem />
                <Related products={nomenclature?.relatedProducts ?? []} />
              </Accordeon>
            )}
          </>
        )}
      </div>
    </div>
  );
};
